import { action, computed, makeObservable, observable, reaction } from "mobx"

import { TourStepBase } from "@src/widgets/onboarding/classes/TourStepBase"
import { ONBOARDING_TOURS_LABEL_MAP } from "@src/widgets/onboarding/constants"
import { ONBOARDING_TOURS, TourContext } from "@src/widgets/onboarding/types"

export abstract class BaseTour {
  enable = true

  constructor() {
    makeObservable(this, {
      activeStep: computed,
      availableSteps: computed,
      closedStep: computed,
      enable: observable,
      isCompleted: computed,
      isInitialStep: computed,
      isLastStep: computed,
      isStarted: computed,
      nextStep: action.bound,
      onClose: action.bound,
      setComplete: action.bound,
      setEnable: action.bound,
      start: action.bound,
    })
  }

  /**
   * Moves to the next step in the tour, if the current step is complete.
   *
   * If the current step is complete, it will be marked as complete,
   * and the next step will be run.
   *
   * Does nothing if the current step is not complete.
   *
   *
   */
  async nextStep() {
    if (this.activeStep) {
      const stepIndex = this.availableSteps.findIndex(
        (s) => this.activeStep?.name === s.name,
      )

      if (stepIndex !== -1) {
        const nextStep = this.availableSteps[stepIndex + 1]

        this.activeStep.setComplete()

        if (nextStep) {
          await nextStep.runAction()
        }
      }
    }
  }

  onClose() {
    if (this.activeStep) {
      this.activeStep.setClosed()
    }
  }

  async prevStep() {
    if (!this.activeStep || this.isInitialStep) {
      return void 0
    }

    const stepIndex = this.availableSteps.findIndex(
      (s) => this.activeStep?.name === s.name,
    )

    if (stepIndex !== -1) {
      const prevStep = this.availableSteps[stepIndex - 1]

      if (prevStep) {
        return prevStep.runAction()
      }
    }
  }

  setComplete() {
    this.steps.forEach((s) => s.setComplete())
  }

  setEnable(v: boolean) {
    this.enable = v
  }

  async start() {
    if (this.closedStep) {
      await this.closedStep.runAction()
    }

    const [first] = this.availableSteps

    if (first) {
      await first.runAction()
    }
  }

  get activeStep(): TourStepBase {
    if (this.isStarted) {
      return this.availableSteps.find((step) => step.inProgress) as TourStepBase
    }

    return this.availableSteps[0] as TourStepBase
  }

  get availableSteps() {
    return this.steps.filter((s) => s.enable)
  }

  get closedStep(): TourStepBase | null {
    return this.availableSteps.find((s: TourStepBase) => s.isClosed) ?? null
  }

  get isCompleted() {
    return this.availableSteps.every((s) => s.isCompleted)
  }

  get isInitialStep() {
    return this.activeStep === this.availableSteps[0]
  }

  get isLastStep() {
    return this.activeStep === this.availableSteps.at(-1)
  }

  /**
   * Determines if the tour has started by checking if any step is active.
   *
   * @returns {boolean} True if any step is active, indicating the tour has started; false otherwise.
   */
  get isStarted(): boolean {
    return this.availableSteps.some((step) => step.inProgress)
  }

  get title() {
    return ONBOARDING_TOURS_LABEL_MAP[this.name]
  }

  abstract name: ONBOARDING_TOURS
  abstract steps: TourStepBase[]
}
